import { useLocation } from "react-router-dom";
import PageHero from "@/components/layout/PageHero";
import SEO from "@/components/layout/SEO";
import ConsultationForm from "@/components/forms/ConsultationForm";
import EligibilityForm from "@/components/forms/EligibilityForm";
import PremiumForm from "@/components/forms/PremiumForm";
import BookButton from "@/components/layout/BookButton";
import { BRAND, WHATSAPP_LINK } from "@/lib/content";
import { Mail, MessageCircle, MapPin, CalendarClock } from "lucide-react";
import { useState } from "react";

export default function Contact() {
  const { hash } = useLocation();
  const initial = hash === "#premium" ? "premium" : hash === "#eligibility" ? "eligibility" : "consultation";
  const [tab, setTab] = useState(initial);

  return (
    <div data-testid="page-contact">
      <SEO
        title="Contact StudyPoland — book a consultation"
        description="Book a consultation with Krzysztof Szymański, check your eligibility, or message us on WhatsApp. Replies within 1–2 business days."
        path="/contact"
      />
      <PageHero
        overline="Contact"
        title={<>Let's talk about your <span className="italic text-[#D7141A]">move to Poland</span>.</>}
        subtitle="Book a time directly, pick the form that fits best, or reach us on WhatsApp or email. We reply within 1–2 business days."
      />

      {/* Setmore prominent CTA */}
      <section className="pb-2" data-testid="contact-setmore-cta">
        <div className="container-sp">
          <div className="rounded-2xl bg-[#0D1B2A] text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-[#1B2A3D]">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#D7141A] flex items-center justify-center shrink-0">
                <CalendarClock className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="overline" style={{ color: "#E5444A" }}>Fastest path</div>
                <h2 className="font-serif text-2xl sm:text-3xl mt-1 leading-tight">Book a consultation directly.</h2>
                <p className="text-white/70 text-sm mt-2 max-w-xl">
                  Pick a time that works for you on our live calendar. Most slots confirmed instantly.
                </p>
              </div>
            </div>
            <BookButton track="general" variant="gold" testId="contact-setmore-cta-button">
              Book Now
            </BookButton>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-sp grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-4">
            <div className="card-sp">
              <div className="overline">Direct contact</div>
              <div className="mt-4 space-y-3 text-sm">
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-[#0D1B2A] hover:text-[#D7141A]" data-testid="contact-email">
                  <Mail className="w-4 h-4" /> {BRAND.email}
                </a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0D1B2A] hover:text-[#D7141A]" data-testid="contact-whatsapp">
                  <MessageCircle className="w-4 h-4" /> {BRAND.whatsappDisplay}
                </a>
                <div className="flex items-center gap-2 text-[#4A5568]">
                  <MapPin className="w-4 h-4" /> Warsaw · Kraków · Remote
                </div>
              </div>
            </div>

            <div className="card-sp">
              <div className="overline">Pick a form</div>
              <div className="mt-4 flex flex-col gap-2">
                {[
                  { key: "eligibility", label: "Eligibility Check" },
                  { key: "consultation", label: "Book Consultation" },
                  { key: "premium", label: "Premium Strategy" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`contact-tab-${t.key}`}
                    className={`text-left px-4 py-3 rounded-xl border transition-all text-sm ${
                      tab === t.key
                        ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                        : "bg-white text-[#0D1B2A] border-[#EFEEE8] hover:border-[#D7141A]/40"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8" data-testid="contact-form-area">
            {tab === "eligibility" && <EligibilityForm testPrefix="contact-eligibility" />}
            {tab === "consultation" && <ConsultationForm testPrefix="contact-consultation" />}
            {tab === "premium" && <PremiumForm testPrefix="contact-premium" />}
          </div>
        </div>
      </section>
    </div>
  );
}
